import React from "react";

import {
    CircularProgress,
    Stack,
    Card,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/joy";
import dayjs from "dayjs";

// components
import BaseApi from "../../utils/BaseApi";
import UserSubscriberContext from "../../context/UserSubscriberContext";

// icons
import EmailIcon from '@mui/icons-material/Email';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import {b64toBlob, downloadBlob} from "../../utils/misc";


function formatShortDate(dateStr) {
    if (!dateStr) return '';
    
    const date = dayjs(dateStr);
    if (!date.isValid()) {
        return '';
    }
    
    const now = dayjs();
    const diffDays = now.diff(date, 'day');
    
    if (diffDays === 0) {
        return date.format('HH:mm');
    } else if (diffDays === 1) {
        return 'Вчера';
    } else if (diffDays < 7) {
        return date.format('ddd');
    } else {
        return date.format('DD.MM');
    }
}

function getFileIcon(mimeType) {
    if (!mimeType) return '📎';
    
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('text')) return '📃';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) return '📦';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📑';
    if (mimeType.includes('json')) return '🔧';
    if (mimeType.includes('xml')) return '🔧';
    
    return '📎';
}

function getFileName(filename) {
    if (!filename) return 'Файл';
    const parts = filename.split('/').pop().split('\\').pop();
    return parts.length > 30 ? parts.substring(0, 30) + '...' : parts;
}


class EmailListItem extends React.Component {
    handleClick = () => {
        this.props.onSelect && this.props.onSelect(this.props.email);
    };

    render() {
        const { email, isSelected } = this.props;

        return (
            <Box
                sx={{
                    cursor: 'pointer',
                    bgcolor: isSelected 
                        ? 'var(--joy-palette-primary-softBg)' 
                        : 'transparent',
                    borderLeft: '3px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: 'var(--joy-palette-primary-softBg)',
                    },
                    position: 'relative',
                    p: 1.5,
                    borderRadius: 1,
                    mb: 0.5,
                }}
                onClick={this.handleClick}
            >
                <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={0}>
                            <Typography
                                level="body-sm" 
                                sx={{
                                    fontWeight: 'normal',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {email.sender || 'Неизвестно'}
                            </Typography>
                        </Stack>
                        <Typography 
                            level="body-xs" 
                            color={'neutral'}
                            sx={{ flexShrink: 0, ml: 1 }}
                        >
                            {formatShortDate(email.created_on)}
                        </Typography>
                    </Stack>

                    <Typography
                        level="body-sm" 
                        sx={{
                            fontWeight: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {email.subject || 'Без темы'}
                    </Typography>

                    <Typography
                        level="body-xs" 
                        color="neutral"
                        sx={{
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4,
                        }}
                    >
                        {this.getPreview(email)}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    getPreview(email) {
        if (email.body) {
            const textContent = email.body
                .replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ')
                .trim();
            return textContent.substring(0, 80) + (textContent.length > 80 ? '...' : '');
        }
        return 'Нет содержания';
    }
}

function EmailAttachmentItem({ attachment }) {
    const api = new BaseApi();
    const [downloading, setDownloading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const handleDownload = async () => {
        if (downloading) return;
        
        setDownloading(true);
        setError(null);
        
        try {

            const response = await api.get(
                `email_attachment/${attachment.id}/download/`,
                {}
            );

            downloadBlob(
                b64toBlob(response.data.file_data),
                response.data.filename,
                response.data.mime_type
            )
        } catch (error) {
            console.error('Ошибка при скачивании:', error);
            setError('Не удалось скачать: ' + (error.response?.data?.error || error.message));
            setTimeout(() => setError(null), 5000);
        } finally {
            setDownloading(false);
        }
    };

    const fileName = getFileName(attachment.filename);

    return (
        <Chip 
            variant="outlined" 
            color="neutral"
            size="lg"
            onClick={handleDownload}
            disabled={downloading}
            startDecorator={<span style={{ fontSize: '1.2em' }}>{getFileIcon(attachment.mime_type)}</span>}
            endDecorator={
                <Tooltip title="Скачать">
                    <IconButton 
                        size="sm" 
                        variant="plain" 
                        color="neutral"
                    >
                        {downloading ? (
                            <CircularProgress size="sm" variant="soft" />
                        ) : (
                            <DownloadIcon />
                        )}
                    </IconButton>
                </Tooltip>
            }
            sx={{ 
                maxWidth: 280,
                '&:hover': {
                    bgcolor: 'background.level1',
                }
            }}
        >
            <Typography 
                level="body-sm" 
                sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                }}
            >
                {fileName}
            </Typography>
        </Chip>
    );
}


function EmailDetail({ email, onBack }) {
    const hasAttachments = email.attachments && email.attachments.length > 0;

    return (
        <Stack spacing={3} sx={{ height: '100%', overflow: 'auto', p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={1}>
                    {hasAttachments && (
                        <Chip 
                            variant="soft" 
                            color="neutral"
                            startDecorator={<AttachFileIcon fontSize="small"/>}
                        >
                            {email.attachments.length} вложение{email.attachments.length > 1 ? 'й' : ''}
                        </Chip>
                    )}
                </Stack>
            </Stack>

            <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                    <Tooltip title="Назад к списку">
                        <IconButton size="sm" variant="soft" onClick={onBack}>
                            <ArrowBackIcon/>
                        </IconButton>
                    </Tooltip>
                    <Typography level="h3" sx={{fontWeight: 'bold'}}>
                        {email.subject || 'Без темы'}
                    </Typography>
                </Stack>
            </Box>

            {/* Metadata */}
            <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1.5}>
                    {/* Sender */}
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.softBg',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <PersonIcon fontSize="small" color="primary"/>
                        </Box>
                        <Stack flex={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography level="body-md" sx={{fontWeight: 'bold'}}>
                                    {email.sender || 'Неизвестно'}
                                </Typography>
                                {email.sender_email && (
                                    <Typography level="body-xs" color="neutral">
                                        &lt;{email.sender_email}&gt;
                                    </Typography>
                                )}
                            </Stack>
                            <Typography level="body-xs" color="neutral">
                                Получатель: {email.recipient || 'Не указан'}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Divider/>

                    {/* Date */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: '50%', 
                            bgcolor: 'neutral.softBg',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <CalendarTodayIcon fontSize="small" color="neutral"/>
                        </Box>
                        <Stack>
                            <Typography level="body-sm">
                                {email.created_on}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>

            {/* Content */}
            <Card variant="outlined" sx={{ 
                p: 3, 
                borderRadius: 2,
                bgcolor: 'background.level1',
            }}>
                {email.body ? (
                    <Box sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        lineHeight: 1.7,
                        '& a': {
                            color: 'primary.solidBg',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            }
                        }
                    }}>
                        {email.body.includes('<') ? (
                            <Box 
                                sx={{ 
                                    '& p': { mb: 1 },
                                    '& strong': { fontWeight: 'bold' },
                                    '& em': { fontStyle: 'italic' },
                                }}
                                dangerouslySetInnerHTML={{ __html: email.body }}
                            />
                        ) : (
                            <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap' }}>
                                {email.body}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Typography level="body-md" color="neutral">
                        Нет содержания
                    </Typography>
                )}
            </Card>

            {hasAttachments && (
                <Stack spacing={2}>
                    <Divider/>
                    <Typography level="title-md" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon color="primary"/>
                        Вложения ({email.attachments.length})
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {email.attachments.map((attachment, index) => (
                            <EmailAttachmentItem 
                                key={attachment.id || index}
                                attachment={attachment}
                            />
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}


class EmailTab extends React.Component {
    static contextType = UserSubscriberContext;

    constructor(props) {
        super(props);
        this.state = {
            emails: null,
            selectedEmail: null,
            loading: true,
            refreshing: false,
        };
    }

    async componentDidMount() {
        await this.retrieveEmails();
    }

    async retrieveEmails() {
        this.setState({ loading: true });
        const url = `email/`;
        const api = new BaseApi();
        const params = {
            entity: this.props.entity,
            entity_id: this.props.entity_id,
            ordering: '-created_on',
        };
        
        try {
            let response = await api.get(url, params);
            const emails = response.data.results || [];
            
            this.setState({
                emails: emails,
                loading: false,
                refreshing: false,
            });
            
            if (emails.length > 0 && !this.state.selectedEmail) {
                this.setState({ selectedEmail: emails[0] });
            }
        } catch (error) {
            console.error('Ошибка загрузки email:', error);
            this.setState({ loading: false, refreshing: false });
        }
    }

    handleRefresh = async () => {
        this.setState({ refreshing: true });
        await this.retrieveEmails();
    };

    handleEmailSelect = (email) => {
        this.setState({ selectedEmail: email });
    };

    render() {
        if (this.state.loading || !this.state.emails) {
            return <CircularProgress/>;
        }

        const { emails, selectedEmail, refreshing } = this.state;

        return (
            <Stack
                direction="row"
                spacing={0}
                sx={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.surface',
                }}
            >
                <Box
                    sx={{
                        width: { xs: '100%', sm: '320px', md: '380px' },
                        borderRight: { sm: '1px solid var(--joy-palette-divider)' },
                        overflowY: 'auto',
                        bgcolor: 'background.level1',
                        display: { xs: selectedEmail ? 'none' : 'flex', sm: 'flex' },
                        flexDirection: 'column',
                    }}
                >
                    {/* List Header */}
                    <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="center" 
                        sx={{ p: 2, pb: 1 }}
                    >
                        <Typography level="title-md" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon color="primary"/>
                            Письма
                        </Typography>
                        <Tooltip title="Обновить">
                            <IconButton 
                                size="sm" 
                                variant="soft" 
                                onClick={this.handleRefresh}
                                disabled={refreshing}
                            >
                                <RefreshIcon sx={{ 
                                    transform: refreshing ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.3s',
                                }}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Divider/>

                    {/* Email List */}
                    <Box sx={{ p: 1 }}>
                        {emails.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <EmailIcon sx={{ fontSize: 48, color: 'neutral.plainColor', mb: 2 }}/>
                                <Typography level="body-md" color="neutral">
                                    Нет писем
                                </Typography>
                                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                                    Письма появятся здесь
                                </Typography>
                            </Box>
                        ) : (
                            emails.map((email) => (
                                <EmailListItem
                                    key={'EmailListItem' + email.id}
                                    email={email}
                                    isSelected={selectedEmail?.id === email.id}
                                    onSelect={this.handleEmailSelect}
                                />
                            ))
                        )}
                    </Box>
                </Box>

                {/* Right Panel - Email Detail */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.surface',
                        display: { xs: selectedEmail ? 'flex' : 'none', sm: 'flex' },
                        flexDirection: 'column',
                    }}
                >
                    {selectedEmail ? (
                        <EmailDetail 
                            email={selectedEmail}
                            onBack={() => this.setState({ selectedEmail: null })}
                        />
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            bgcolor: 'background.level1',
                        }}>
                            <Stack spacing={2} sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ 
                                    width: 80, 
                                    height: 80, 
                                    borderRadius: '50%', 
                                    bgcolor: 'neutral.softBg',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                }}>
                                    <EmailIcon sx={{ fontSize: 40, color: 'neutral.plainColor' }}/>
                                </Box>
                                <Typography level="title-lg" color="neutral">
                                    Выберите письмо
                                </Typography>
                                <Typography level="body-sm" color="neutral">
                                    Выберите письмо из списка для просмотра
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Stack>
        );
    }
}

export default EmailTab;
